import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Collapse, Form, Input, InputNumber, Select, Tooltip } from "antd";
import type {
  TCommunicationChangeAction,
  TCommunicationAuditEntry,
  TCommunicationQuestion,
  TCommunicationReferenceOption,
  TCommunicationSuggestionStatus,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "../../helpers/communication-types-helper";
import {
  buildCommunicationLogisticsPreview,
} from "../../helpers/communication-preview-helper";
import { formatCommunicationDateTime } from "../../helpers/communication-date-helper";
import { localizeCommunicationLookupValue } from "../../helpers/communication-localize-helper";
import CommunicationOriginalEmailField from "../../fields/communication-original-email-field";
import CommunicationConst from "../../communication-const";
import { Localize, localizeByKeyStatic } from "components/service";
import Icons from "components/icons";

const { TextArea } = Input;

interface ICommunicationTaskFormProps {
  task: TCommunicationTask;
  canEditRaw: boolean;
  canApprove: boolean;
  isAuditView?: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntry?: TCommunicationAuditEntry;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
  action?: () => void;
  showModal?: boolean;
}

const actionOptions = [
  { value: "add", label: localizeByKeyStatic("COMMUNICATION.Action_Add") },
  { value: "remove", label: localizeByKeyStatic("COMMUNICATION.Action_Remove") },
  { value: "change", label: localizeByKeyStatic("COMMUNICATION.Action_Change") },
];

const actionLabelKeyMap: Record<TCommunicationChangeAction, string> = {
  add: "COMMUNICATION.Action_Add",
  remove: "COMMUNICATION.Action_Remove",
  change: "COMMUNICATION.Action_Change",
};

const questionStatusOptions = [
  { value: "open", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open") },
  { value: "reviewed", label: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed") },
];

const questionStatusLabelMap: Record<TCommunicationQuestion["status"], string> = {
  open: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Open"),
  reviewed: localizeByKeyStatic("COMMUNICATION.QuestionStatus_Reviewed"),
};

const suggestionStatusLabelKeyMap: Record<TCommunicationSuggestionStatus, string> = {
  pending: "COMMUNICATION.EquipmentSuggestion_Pending",
  confirmed: "COMMUNICATION.EquipmentSuggestion_Confirmed",
  changed: "COMMUNICATION.EquipmentSuggestion_Changed",
};

const createEmptyEquipmentChange = (taskId: string) => ({
  id: `new-${taskId}-${Date.now()}`,
  action: "add" as const,
  item_label: "",
  qty: 1,
  color: null,
  equipment_group_id: undefined,
  suggested_equipment_group_id: undefined,
  suggestion_status: undefined,
});

const createEmptyQuestion = (taskId: string): TCommunicationQuestion => ({
  id: `question-${taskId}-${Date.now()}`,
  text: "",
  status: "open",
});

const normalizeCollapseKeys = (keys?: string | string[] | null): string[] =>
  Array.isArray(keys) ? keys : keys ? [ keys ] : [];

const hasStandUpdates = (standUpdates?: Partial<TCommunicationTaskFormValues["stand_updates"]> | null): boolean =>
  Boolean(
    (typeof standUpdates?.flooring_id === "string"
      ? standUpdates.flooring_id.trim()
      : standUpdates?.flooring_id)
    || (typeof standUpdates?.flooring_color === "string"
      ? standUpdates.flooring_color.trim()
      : standUpdates?.flooring_color)
  );

const hasManagerNote = (managerNote?: string | null): boolean =>
  Boolean(managerNote?.trim());

const resolveEquipmentGroupId = (
  equipmentGroupId: string | null | undefined,
  equipmentId: string | null | undefined,
  equipmentIdToGroupIdMap: Map<string, string>
): string | undefined => {
  if (equipmentGroupId?.trim()) {
    return equipmentGroupId;
  }

  if (equipmentId?.trim()) {
    return equipmentIdToGroupIdMap.get(equipmentId);
  }

  return undefined;
};

const buildMergedEquipmentOptions = (
  equipmentOptions: TCommunicationReferenceOption[],
  equipmentChanges: TCommunicationTask["final_changes"]["equipment_changes"],
  equipmentIdToGroupIdMap: Map<string, string>
): TCommunicationReferenceOption[] => {
  const optionMap = new Map(
    equipmentOptions.map((option) => [ option.value, option ])
  );

  equipmentChanges.forEach((change) => {
    const resolvedGroupId = resolveEquipmentGroupId(
      change.equipment_group_id || change.suggested_equipment_group_id,
      change.equipment_id,
      equipmentIdToGroupIdMap
    );

    if (!resolvedGroupId || optionMap.has(resolvedGroupId)) {
      return;
    }

    optionMap.set(resolvedGroupId, {
      value: resolvedGroupId,
      label: change.equipment_title || change.item_label || resolvedGroupId,
      rawId: change.equipment_id,
    });
  });

  return Array.from(optionMap.values());
};

type TEquipmentSuggestionMeta = {
  suggestedGroupId: string;
  status: TCommunicationSuggestionStatus;
};

const getEquipmentSuggestionMeta = (
  change: TCommunicationTaskFormValues["equipment_changes"][number]
): TEquipmentSuggestionMeta | null => {
  if (!change?.suggested_equipment_group_id) {
    return null;
  }

  return {
    suggestedGroupId: change.suggested_equipment_group_id,
    status: change.suggestion_status || "pending",
  };
};

const mapTaskToFormValues = (
  task: TCommunicationTask,
  equipmentIdToGroupIdMap: Map<string, string> = new Map()
): TCommunicationTaskFormValues => ({
  event_name: task.event_name || "",
  company_name: task.company_name || "",
  stand_number: task.stand_number || "",
  organizer_id: task.organizer_id || undefined,
  exhibition_type_id: task.exhibition_type_id || undefined,
  manager_note: task.final_changes.manager_note || "",
  equipment_changes:
    task.final_changes.equipment_changes.length > 0
      ? task.final_changes.equipment_changes.map((change) => {
        const suggestedEquipmentGroupId = resolveEquipmentGroupId(
          change.suggested_equipment_group_id || change.equipment_group_id,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );
        const resolvedEquipmentGroupId = resolveEquipmentGroupId(
          change.equipment_group_id || suggestedEquipmentGroupId,
          change.equipment_id,
          equipmentIdToGroupIdMap
        );

        return {
          ...change,
          color: localizeCommunicationLookupValue(change.color) || null,
          equipment_group_id: resolvedEquipmentGroupId,
          suggested_equipment_group_id: suggestedEquipmentGroupId,
          suggestion_status:
            suggestedEquipmentGroupId
              ? change.suggestion_status || "pending"
              : change.suggestion_status || undefined,
        };
      })
      : [createEmptyEquipmentChange(task.id)],
  stand_updates: {
    flooring_id: task.final_changes.stand_updates.flooring_id || undefined,
    flooring_color:
      localizeCommunicationLookupValue(task.final_changes.stand_updates.flooring_color) || "",
  },
  questions: task.questions.length > 0 ? task.questions : [],
});

const getInitialStep = (task: TCommunicationTask): number => {
  if (task.status === CommunicationConst.statuses.APPROVED) {
    return 3;
  }

  return task.event_name?.trim() && task.company_name?.trim() && task.stand_number?.trim() ? 2 : 1;
};

const CommunicationTaskForm: React.FC<ICommunicationTaskFormProps> = ({
  task,
  canApprove,
  equipmentOptions,
  flooringOptions,
  organizerOptions,
  exhibitionOptions,
  auditEntry,
  isAuditView,
  onApprove,
  action,
  showModal,
}) => {
  const [form] = Form.useForm<TCommunicationTaskFormValues>();
  const [currentStep, setCurrentStep] = useState<number>(getInitialStep(task));
  const [openEquipmentField, setOpenEquipmentField] = useState<number | null>(null);
  const [openContextPanels, setOpenContextPanels] = useState<string[]>([]);
  const [showStandUpdates, setShowStandUpdates] = useState<boolean>(
    hasStandUpdates(task.extracted_changes.stand_updates) || hasStandUpdates(task.final_changes.stand_updates)
  );
  const [showManagerNote, setShowManagerNote] = useState<boolean>(
    hasManagerNote(task.final_changes.manager_note)
  );
  const equipmentIdToGroupIdMap = useMemo(
    () =>
      new Map(
        equipmentOptions
          .filter((option) => option.rawId)
          .map((option) => [ option.rawId as string, option.value ])
      ),
    [equipmentOptions]
  );

  const watchedQuestions = Form.useWatch("questions", form) || [];
  const watchedEventName = Form.useWatch("event_name", form);
  const watchedCompanyName = Form.useWatch("company_name", form);
  const watchedStandNumber = Form.useWatch("stand_number", form);
  const watchedEquipmentChanges = Form.useWatch("equipment_changes", form) || [];
  const watchedStandUpdates = Form.useWatch("stand_updates", form) || {};
  const openQuestionsCount = watchedQuestions.filter((question) => question?.status === "open").length;
  const pendingEquipmentSuggestionsCount = watchedEquipmentChanges.filter((change) => {
    const suggestionMeta = getEquipmentSuggestionMeta(change);
    return suggestionMeta?.status === "pending";
  }).length;
  const isMatchComplete = Boolean(
    watchedEventName?.trim() && watchedCompanyName?.trim() && watchedStandNumber?.trim()
  );

  useEffect(() => {
    if (showModal === false) {
      return;
    }

    form.resetFields();
    form.setFieldsValue(mapTaskToFormValues(task, equipmentIdToGroupIdMap));
    setCurrentStep(getInitialStep(task));
    setOpenEquipmentField(null);
    setOpenContextPanels([]);
    setShowStandUpdates(
      hasStandUpdates(task.extracted_changes.stand_updates)
      || hasStandUpdates(task.final_changes.stand_updates)
    );
    setShowManagerNote(hasManagerNote(task.final_changes.manager_note));
  }, [form, showModal, task]);

  useEffect(() => {
    if (showModal === false || !equipmentIdToGroupIdMap.size) {
      return;
    }

    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasHydratedEquipmentMetadata = false;

    const hydratedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change?.equipment_id) {
        const nextSuggestedGroupId = change?.suggested_equipment_group_id;
        const nextEquipmentGroupId =
          change?.equipment_group_id
          || (change?.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

        if (
          nextEquipmentGroupId === change?.equipment_group_id
          && nextSuggestedGroupId === change?.suggested_equipment_group_id
          && change?.suggestion_status
        ) {
          return change;
        }

        hasHydratedEquipmentMetadata = true;

        return {
          ...change,
          equipment_group_id: nextEquipmentGroupId,
          suggested_equipment_group_id: nextSuggestedGroupId,
          suggestion_status:
            nextSuggestedGroupId
              ? change?.suggestion_status || "pending"
              : change?.suggestion_status,
        };
      }

      const resolvedGroupId = equipmentIdToGroupIdMap.get(change.equipment_id);
      const nextSuggestedGroupId =
        change.suggested_equipment_group_id || resolvedGroupId;
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (change.suggestion_status !== "changed" ? nextSuggestedGroupId : undefined);

      if (!nextEquipmentGroupId && !nextSuggestedGroupId) {
        return change;
      }

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextSuggestedGroupId === change.suggested_equipment_group_id
        && change.suggestion_status
      ) {
        return change;
      }

      hasHydratedEquipmentMetadata = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        suggested_equipment_group_id: nextSuggestedGroupId,
        suggestion_status:
          nextSuggestedGroupId
            ? change.suggestion_status || "pending"
            : change.suggestion_status,
      };
    });

    if (hasHydratedEquipmentMetadata) {
      form.setFieldValue("equipment_changes", hydratedChanges);
    }
  }, [equipmentIdToGroupIdMap, form, showModal]);

  useEffect(() => {
    const nextMergedEquipmentOptions = buildMergedEquipmentOptions(
      equipmentOptions,
      task.final_changes.equipment_changes,
      equipmentIdToGroupIdMap
    );

    if (showModal === false || !nextMergedEquipmentOptions.length) {
      return;
    }

    const optionMap = new Map(
      nextMergedEquipmentOptions.map((option) => [ option.value, option.label ])
    );
    const currentChanges = form.getFieldValue("equipment_changes") || [];
    let hasPatchedSuggestions = false;

    const patchedChanges = currentChanges.map((change: TCommunicationTaskFormValues["equipment_changes"][number]) => {
      if (!change) {
        return change;
      }

      const shouldAutofillSuggestion = change.suggestion_status !== "changed";
      const nextEquipmentGroupId =
        change.equipment_group_id
        || (shouldAutofillSuggestion ? change.suggested_equipment_group_id : undefined);
      const nextItemLabel =
        change.item_label || (nextEquipmentGroupId ? optionMap.get(nextEquipmentGroupId) || "" : "");

      if (
        nextEquipmentGroupId === change.equipment_group_id
        && nextItemLabel === change.item_label
      ) {
        return change;
      }

      hasPatchedSuggestions = true;

      return {
        ...change,
        equipment_group_id: nextEquipmentGroupId,
        item_label: nextItemLabel,
      };
    });

    if (hasPatchedSuggestions) {
      form.setFieldValue("equipment_changes", patchedChanges);
    }
  }, [equipmentIdToGroupIdMap, equipmentOptions, form, showModal, task.final_changes.equipment_changes]);

  const steps = useMemo(() => {
    return [
      {
        id: 1,
        title: localizeByKeyStatic("COMMUNICATION.Step_Matching_Title"),
        description: localizeByKeyStatic("COMMUNICATION.Step_Matching_Help"),
      },
      {
        id: 2,
        title: localizeByKeyStatic("COMMUNICATION.Step_Changes_Title"),
        description: localizeByKeyStatic("COMMUNICATION.Step_Changes_Help"),
      },
      {
        id: 3,
        title: localizeByKeyStatic("COMMUNICATION.Step_Questions_Title"),
        description: localizeByKeyStatic("COMMUNICATION.Step_Questions_Help"),
      },
    ];
  }, []);

  const mergedEquipmentOptions = useMemo(
    () =>
      buildMergedEquipmentOptions(
        equipmentOptions,
        task.final_changes.equipment_changes,
        equipmentIdToGroupIdMap
      ),
    [ equipmentIdToGroupIdMap, equipmentOptions, task.final_changes.equipment_changes ]
  );

  const equipmentOptionsMap = useMemo(
    () => new Map(mergedEquipmentOptions.map((option) => [ option.value, option.label ])),
    [mergedEquipmentOptions]
  );

  const equipmentSelectOptions = useMemo(
    () =>
      mergedEquipmentOptions.map((option) => ({
        ...option,
        searchLabel: option.label,
      })),
    [mergedEquipmentOptions]
  );

  const finalPreviewEquipmentChanges = useMemo(() => {
    const watchedChanges = watchedEquipmentChanges || [];
    const formChanges =
      (form.getFieldValue("equipment_changes") || []) as TCommunicationTaskFormValues["equipment_changes"];
    const sourceChanges =
      watchedChanges.length
        ? watchedChanges
        : formChanges.length
          ? formChanges
          : task.final_changes.equipment_changes;

    return sourceChanges
      .map((change) => {
        const resolvedEquipmentGroupId =
          change?.equipment_group_id || change?.suggested_equipment_group_id || undefined;
        const nextItemLabel =
          (resolvedEquipmentGroupId
            ? equipmentOptionsMap.get(resolvedEquipmentGroupId) || ""
            : "")
          || change?.item_label?.trim()
          || change?.equipment_title
          || "";

        return {
          ...change,
          equipment_group_id: resolvedEquipmentGroupId,
          item_label: nextItemLabel,
        };
      })
      .filter((change) => change?.item_label?.trim());
  }, [
    currentStep,
    equipmentOptionsMap,
    form,
    showModal,
    task.final_changes.equipment_changes,
    watchedEquipmentChanges,
  ]);

  const originalEmailEquipmentChanges = useMemo(
    () => [
      ...task.extracted_changes.equipment_changes,
      ...finalPreviewEquipmentChanges,
    ],
    [ finalPreviewEquipmentChanges, task.extracted_changes.equipment_changes ]
  );
  const isOriginalEmailExpanded = openContextPanels.includes("ai-context");
  const originalEmailPanelLabel = useMemo(
    () => (
      <div className="communication-original-email-collapse__trigger">
        <span className="communication-original-email-collapse__trigger-icon">
          <Icons.Document />
        </span>
        <span className="communication-original-email-collapse__trigger-copy">
          <strong>{localizeByKeyStatic("COMMUNICATION.Section_AIContextSecondary")}</strong>
        </span>
      </div>
    ),
    []
  );

  const finalPreviewSource = useMemo(
    () => ({
      event_name: watchedEventName ?? task.event_name,
      company_name: watchedCompanyName ?? task.company_name,
      stand_number: watchedStandNumber ?? task.stand_number,
      received_at: task.received_at,
      changes: {
        equipment_changes: finalPreviewEquipmentChanges,
        stand_updates: watchedStandUpdates || {},
        manager_note: "",
      },
    }),
    [
      finalPreviewEquipmentChanges,
      task.company_name,
      task.event_name,
      task.received_at,
      task.stand_number,
      watchedCompanyName,
      watchedEventName,
      watchedStandNumber,
      watchedStandUpdates,
    ]
  );

  const extractedPreviewSource = useMemo(
    () => ({
      event_name: watchedEventName ?? task.event_name,
      company_name: watchedCompanyName ?? task.company_name,
      stand_number: watchedStandNumber ?? task.stand_number,
      received_at: task.received_at,
      changes: task.extracted_changes,
    }),
    [
      task.company_name,
      task.event_name,
      task.extracted_changes,
      task.received_at,
      task.stand_number,
      watchedCompanyName,
      watchedEventName,
      watchedStandNumber,
    ]
  );

  const extractedLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(extractedPreviewSource),
    [extractedPreviewSource]
  );

  const finalLogisticsPreview = useMemo(
    () => buildCommunicationLogisticsPreview(finalPreviewSource),
    [finalPreviewSource]
  );
  const isLastStep = currentStep === steps.length;

  const patchEquipmentChange = (
    index: number,
    patch: Partial<TCommunicationTaskFormValues["equipment_changes"][number]>
  ) => {
    const equipmentChanges = form.getFieldValue("equipment_changes") || [];
    const nextEquipmentChanges = equipmentChanges.map(
      (
        change: TCommunicationTaskFormValues["equipment_changes"][number],
        changeIndex: number
      ) => (changeIndex === index ? { ...change, ...patch } : change)
    );

    form.setFieldValue("equipment_changes", nextEquipmentChanges);
  };

  const handleQuestionStatusToggle = (index: number, checked: boolean) => {
    form.setFieldValue(
      [ "questions", index, "status" ],
      checked ? "reviewed" : "open"
    );
  };

  const syncEquipmentChangeLabel = (index: number, equipmentGroupId?: string) => {
    const nextLabel = equipmentGroupId ? equipmentOptionsMap.get(equipmentGroupId) || "" : "";
    patchEquipmentChange(index, { item_label: nextLabel });
  };

  const handleConfirmEquipmentSuggestion = (index: number) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const nextEquipmentGroupId =
      currentChange.equipment_group_id || currentChange.suggested_equipment_group_id;

    patchEquipmentChange(index, {
      equipment_group_id: nextEquipmentGroupId,
      suggestion_status: "confirmed",
    });
    syncEquipmentChangeLabel(index, nextEquipmentGroupId);
  };

  const handleChooseAnotherEquipment = (index: number) => {
    patchEquipmentChange(index, {
      equipment_group_id: undefined,
      item_label: "",
      suggestion_status: "changed",
    });
    setOpenEquipmentField(index);
  };

  const handleEquipmentSelectChange = (index: number, equipmentGroupId?: string) => {
    const currentChange = form.getFieldValue([ "equipment_changes", index ]) || {};
    const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

    patchEquipmentChange(index, {
      equipment_group_id: equipmentGroupId,
      suggestion_status:
        suggestionMeta
          ? equipmentGroupId && equipmentGroupId === suggestionMeta.suggestedGroupId
            ? "pending"
            : "changed"
          : currentChange.suggestion_status,
    });
    syncEquipmentChangeLabel(index, equipmentGroupId);

    if (equipmentGroupId) {
      setOpenEquipmentField(null);
    }
  };

  const renderLogisticsTable = (
    rows: ReturnType<typeof buildCommunicationLogisticsPreview>,
    emptyKey: string,
    productLabelKey = "COMMUNICATION.Preview_Product"
  ) => {
    if (!rows.length) {
      return (
        <div className="communication-task-form__preview-empty">
          <Localize>{emptyKey}</Localize>
        </div>
      );
    }

    return (
      <div className="communication-task-form__logistics-table">
        <div className="communication-task-form__logistics-head">
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("COMMUNICATION.Filter_Event")}
            >
              <Localize>COMMUNICATION.Filter_Event</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--action">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("FORMS.Input_Label_Action")}
            >
              <Localize>FORMS.Input_Label_Action</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic(productLabelKey)}
            >
              <Localize>{productLabelKey}</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("FORMS.Input_Label_CompanyName")}
            >
              <Localize>FORMS.Input_Label_CompanyName</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("FORMS.Input_Label_StandNumber")}
            >
              <Localize>FORMS.Input_Label_StandNumber</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--qty">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("COMMUNICATION.Preview_TotalQty")}
            >
              <Localize>COMMUNICATION.Preview_TotalQty</Localize>
            </span>
          </strong>
          <strong className="communication-task-form__logistics-head-cell communication-task-form__logistics-head-cell--date">
            <span
              className="communication-task-form__logistics-head-copy"
              title={localizeByKeyStatic("COMMUNICATION.Preview_EmailDate")}
            >
              <Localize>COMMUNICATION.Preview_EmailDate</Localize>
            </span>
          </strong>
        </div>

        {rows.map((row) => (
            <div key={row.id} className="communication-task-form__logistics-row">
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.event_name || "\u2014"}
              >
                {row.event_name || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell communication-task-form__logistics-cell--action">
              <span
                className={`communication-task-form__action-tag communication-task-form__action-tag--${row.action}`}
              >
                <Localize>{actionLabelKeyMap[row.action]}</Localize>
              </span>
            </span>
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.product || "\u2014"}
              >
                {row.product || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.company_name || "\u2014"}
              >
                {row.company_name || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell">
              <span
                className="communication-task-form__logistics-cell-copy"
                title={row.stand_number || "\u2014"}
              >
                {row.stand_number || "\u2014"}
              </span>
            </span>
            <span className="communication-task-form__logistics-cell communication-task-form__logistics-cell--qty">
              {row.quantity}
            </span>
            <span className="communication-task-form__logistics-cell communication-task-form__logistics-cell--date">
              {row.received_at}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="model-form communication-task-form"
      initialValues={mapTaskToFormValues(task, equipmentIdToGroupIdMap)}
    >
      <div className="communication-task-form__meta">
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_Sender</Localize></span>
          <strong className="communication-task-form__meta-value" title={task.sender}>
            {task.sender}
          </strong>
        </div>
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_ReceivedAt</Localize></span>
          <strong className="communication-task-form__meta-value">
            {formatCommunicationDateTime(task.received_at)}
          </strong>
        </div>
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_Subject</Localize></span>
          <strong className="communication-task-form__meta-value" title={task.subject}>
            {task.subject}
          </strong>
        </div>
        <div className="communication-task-form__meta-item">
          <span><Localize>FORMS.Input_Label_Confidence</Localize></span>
          <strong className="communication-task-form__meta-value">{`${task.confidence}%`}</strong>
        </div>
      </div>

      <div className="communication-task-form__steps">
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            className={`communication-task-form__step ${currentStep === step.id ? "is-active" : ""} ${
              currentStep > step.id ? "is-complete" : ""
            }`}
            onClick={() => setCurrentStep(step.id)}
          >
            <span className="communication-task-form__step-number">{step.id}</span>
            <span className="communication-task-form__step-text">{step.title}</span>
          </button>
        ))}
      </div>

      <div className="communication-task-form__step-help">
        <strong>{steps.find((step) => step.id === currentStep)?.title}</strong>
        <span>{steps.find((step) => step.id === currentStep)?.description}</span>
      </div>

      {currentStep === 3 && (
        <div className="communication-task-form__preview-card communication-task-form__preview-card--full">
          <div className="communication-task-form__preview-header">
            <span className="communication-task-form__preview-title">
              <Localize>COMMUNICATION.Preview_LogisticsFinal</Localize>
            </span>
            <span className="communication-task-form__preview-count">
              <Localize count={finalLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
            </span>
          </div>
          <small className="communication-task-form__preview-hint">
            <Localize>COMMUNICATION.Preview_LogisticsFinalHint</Localize>
          </small>

          {renderLogisticsTable(
            finalLogisticsPreview,
            "COMMUNICATION.Text_NoFinalChanges"
          )}
        </div>
      )}

      {currentStep === 1 && (
        <div className="communication-task-form__section">
          <div className="row-grid grid-gap-10 row-gap-0 col-md-2">
            <Form.Item name="event_name" label={<Localize>FORMS.Input_Label_EventName</Localize>}>
              <Input />
            </Form.Item>
            <Form.Item name="company_name" label={<Localize>FORMS.Input_Label_CompanyName</Localize>}>
              <Input />
            </Form.Item>
            <Form.Item name="stand_number" label={<Localize>FORMS.Input_Label_StandNumber</Localize>}>
              <Input />
            </Form.Item>
            <Form.Item name="organizer_id" label={<Localize>FORMS.Input_Label_Organizer</Localize>}>
              <Select
                allowClear
                options={organizerOptions}
                suffixIcon={<Icons.Arrow />}
                placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
              />
            </Form.Item>
            <Form.Item
              name="exhibition_type_id"
              label={<Localize>FORMS.Input_Label_ExhibitionTypes</Localize>}
            >
              <Select
                allowClear
                options={exhibitionOptions}
                suffixIcon={<Icons.Arrow />}
                placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
              />
            </Form.Item>
          </div>
          {!isMatchComplete && (
            <Alert
              showIcon
              type="warning"
              message={localizeByKeyStatic("COMMUNICATION.Match_NotReady")}
            />
          )}
        </div>
      )}

      {currentStep === 2 && (
        <div className="communication-task-form__section">
          <div className="communication-task-form__preview-card communication-task-form__preview-card--full">
            <div className="communication-task-form__preview-header">
              <span className="communication-task-form__preview-title">
                <Localize>COMMUNICATION.Preview_LogisticsExtracted</Localize>
              </span>
              <span className="communication-task-form__preview-count">
                <Localize count={extractedLogisticsPreview.length}>COMMUNICATION.Text_ChangesCount</Localize>
              </span>
            </div>
            <small className="communication-task-form__preview-hint">
              <Localize>COMMUNICATION.Preview_LogisticsExtractedHint</Localize>
            </small>

            {renderLogisticsTable(
              extractedLogisticsPreview,
              "COMMUNICATION.Text_NoExtractedChanges",
              "COMMUNICATION.Preview_ItemFromEmail"
            )}
          </div>

          {pendingEquipmentSuggestionsCount > 0 && (
            <Alert
              className="communication-task-form__suggestions-alert"
              showIcon
              type="warning"
              message={
                <Localize count={pendingEquipmentSuggestionsCount}>
                  COMMUNICATION.EquipmentSuggestion_PendingSummary
                </Localize>
              }
            />
          )}

          <Form.List name="equipment_changes">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  const currentChange = watchedEquipmentChanges?.[field.name] || {};
                  const equipmentLabel =
                    equipmentOptionsMap.get(currentChange?.equipment_group_id || "") || "";
                  const suggestionMeta = getEquipmentSuggestionMeta(currentChange);

                  return (
                    <div
                      key={field.key}
                      className={`row-grid grid-gap-10 row-gap-0 communication-task-form__row communication-task-form__change-grid${suggestionMeta ? ` communication-task-form__change-grid--${suggestionMeta.status}` : ""}`}
                    >
                    <Form.Item
                      name={[field.name, "item_label"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "equipment_id"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "equipment_title"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "suggested_equipment_group_id"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "suggestion_status"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "equipment_group_id"]}
                      hidden={true}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "action"]}
                      label={<Localize>FORMS.Input_Label_Action</Localize>}
                    >
                      <Select suffixIcon={<Icons.Arrow />} options={actionOptions} />
                    </Form.Item>
                    <Form.Item
                      label={
                        <div className="communication-task-form__equipment-label">
                          <span className="communication-task-form__equipment-label-text">
                            <Localize>FORMS.Input_Label_Equipment</Localize>
                          </span>

                          {suggestionMeta && (
                            <div
                              className={`communication-task-form__equipment-suggestion communication-task-form__equipment-suggestion--${suggestionMeta.status}`}
                            >
                              <span className="communication-task-form__equipment-suggestion-badge">
                                <Localize>
                                  {suggestionMeta.status === "pending"
                                    ? "COMMUNICATION.EquipmentSuggestion_Badge"
                                    : suggestionMeta.status === "confirmed"
                                      ? "COMMUNICATION.EquipmentSuggestion_ConfirmedBadge"
                                      : "COMMUNICATION.EquipmentSuggestion_ChangedBadge"}
                                </Localize>
                              </span>

                              {suggestionMeta.status === "pending" ? (
                                <div className="communication-task-form__equipment-suggestion-actions">
                                  <Button
                                    type="link"
                                    icon={<Icons.Check />}
                                    onClick={() => handleConfirmEquipmentSuggestion(field.name)}
                                  >
                                    <Localize>GLOBAL.Button_Text_Confirm</Localize>
                                  </Button>
                                  <Button
                                    type="link"
                                    onClick={() => handleChooseAnotherEquipment(field.name)}
                                  >
                                    <Localize>COMMUNICATION.Button_ChooseAnother</Localize>
                                  </Button>
                                </div>
                              ) : (
                                <span className="communication-task-form__equipment-suggestion-status">
                                  <Localize>{suggestionStatusLabelKeyMap[suggestionMeta.status]}</Localize>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      }
                    >
                      <div className="communication-task-form__equipment-field">
                        <Tooltip title={equipmentLabel.length > 40 ? equipmentLabel : undefined}>
                          <Select
                            key={`${field.key}-${currentChange?.equipment_group_id || currentChange?.suggested_equipment_group_id || "empty"}-${mergedEquipmentOptions.length}`}
                            suffixIcon={<Icons.Arrow />}
                            allowClear
                            showSearch
                            open={openEquipmentField === field.name ? true : undefined}
                            value={currentChange?.equipment_group_id || undefined}
                            options={equipmentSelectOptions}
                            optionLabelProp="label"
                            optionFilterProp="searchLabel"
                            popupClassName="communication-task-form__equipment-dropdown"
                            placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                            onChange={(value) => handleEquipmentSelectChange(field.name, value)}
                            onDropdownVisibleChange={(isOpen) =>
                              setOpenEquipmentField(isOpen ? field.name : null)
                            }
                          />
                        </Tooltip>
                      </div>
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "qty"]}
                      label={<Localize>FORMS.Input_Label_Amount</Localize>}
                      rules={[{ required: true, message: localizeByKeyStatic("FORM_RULES.Required_Amount") }]}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "color"]}
                      label={<Localize>FORMS.Input_Label_Color</Localize>}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item label=" ">
                      <Button
                        icon={<Icons.Delete />}
                        onClick={() => {
                          setOpenEquipmentField(null);
                          remove(field.name);
                        }}
                      />
                    </Form.Item>
                    </div>
                  );
                })}

                <div className="communication-task-form__add-actions">
                  <div className="communication-task-form__add-actions-primary">
                    <Button
                      className="light-bg"
                      icon={<Icons.Plus />}
                      onClick={() => add(createEmptyEquipmentChange(task.id))}
                    >
                      <span className="ellipsis">
                        <Localize>COMMUNICATION.Button_AddChange</Localize>
                      </span>
                    </Button>
                  </div>

                  {/*
                  (!showStandUpdates || !showManagerNote) && (
                    <div className="communication-task-form__add-actions-secondary">
                      {!showStandUpdates && (
                        <Button
                          className="light-bg"
                          icon={<Icons.Plus />}
                          onClick={() => setShowStandUpdates(true)}
                        >
                          <span className="ellipsis">
                            <Localize>COMMUNICATION.Button_AddStandUpdate</Localize>
                          </span>
                        </Button>
                      )}

                      {!showManagerNote && (
                        <Button
                          className="light-bg"
                          icon={<Icons.Plus />}
                          onClick={() => setShowManagerNote(true)}
                        >
                          <span className="ellipsis">
                            <Localize>COMMUNICATION.Button_AddManagerNote</Localize>
                          </span>
                        </Button>
                      )}
                    </div>
                  )}
                  */}
                </div>
              </>
            )}
          </Form.List>

          {showStandUpdates && (
            <div className="row-grid grid-gap-10 row-gap-0 col-md-2 communication-task-form__flooring">
              <Form.Item
                name={["stand_updates", "flooring_id"]}
                label={<Localize>FORMS.Input_Label_Flooring</Localize>}
              >
                <Select
                  suffixIcon={<Icons.Arrow />}
                  allowClear
                  options={flooringOptions}
                  placeholder={localizeByKeyStatic("FORMS.Input_Placeholder_Choose")}
                />
              </Form.Item>
              <Form.Item
                name={["stand_updates", "flooring_color"]}
                label={<Localize>FORMS.Input_Label_CarpetColor</Localize>}
              >
                <Input />
              </Form.Item>
            </div>
          )}

          {showManagerNote && (
            <Form.Item name="manager_note" label={<Localize>FORMS.Input_Label_ManagerNote</Localize>}>
              <TextArea rows={4} />
            </Form.Item>
          )}
        </div>
      )}

      {currentStep === 3 && (
        <div className="communication-task-form__section">
          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <div className="communication-task-form__questions-panel">
                <div className="communication-task-form__questions-header">
                  <div className="communication-task-form__questions-copy">
                    <span className="communication-task-form__questions-eyebrow">
                      <Localize>COMMUNICATION.Preview_QuestionsOverview</Localize>
                    </span>
                    <strong className="communication-task-form__questions-title">
                      <Localize
                        questions={watchedQuestions.length}
                        open={openQuestionsCount}
                      >
                        COMMUNICATION.Text_QuestionsSummary
                      </Localize>
                    </strong>
                    <span className="communication-task-form__questions-help">
                      <Localize>COMMUNICATION.Step_Questions_Help</Localize>
                    </span>
                  </div>

                  <div className="communication-task-form__questions-actions">
                    <span
                      className={`communication-task-form__questions-badge${openQuestionsCount > 0 ? " communication-task-form__questions-badge--warning" : ""}`}
                    >
                      {fields.length > 0 ? (
                        <Localize
                          questions={watchedQuestions.length}
                          open={openQuestionsCount}
                        >
                          COMMUNICATION.Text_QuestionsSummary
                        </Localize>
                      ) : (
                        <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                      )}
                    </span>

                    <Button
                      className="light-bg"
                      icon={<Icons.Plus />}
                      onClick={() => add(createEmptyQuestion(task.id))}
                    >
                      <span className="ellipsis">
                        <Localize>COMMUNICATION.Button_AddQuestion</Localize>
                      </span>
                    </Button>
                  </div>
                </div>

                <div className="communication-task-form__question-list">
                  {fields.length === 0 && (
                    <div className="communication-task-form__questions-empty">
                      <Localize>COMMUNICATION.Text_NoQuestions</Localize>
                    </div>
                  )}

                  {fields.map((field, index) => {
                    const currentQuestion = watchedQuestions?.[field.name];
                    const questionStatus = currentQuestion?.status || "open";

                    return (
                      <div
                        key={field.key}
                        className={`communication-task-form__question-card communication-task-form__question-card--${questionStatus}`}
                      >
                        <div className="communication-task-form__question-card-header">
                          <div className="communication-task-form__question-card-copy">
                            <strong className="communication-task-form__question-card-title">
                              {`${localizeByKeyStatic("COMMUNICATION.Field_Question")} #${index + 1}`}
                            </strong>
                            <span
                              className={`communication-task-form__question-card-state communication-task-form__question-card-state--${questionStatus}`}
                            >
                              {questionStatusLabelMap[questionStatus]}
                            </span>
                          </div>

                          <div className="communication-task-form__question-card-actions">
                            <Checkbox
                              className="communication-task-form__question-toggle"
                              checked={questionStatus === "reviewed"}
                              onChange={(event) =>
                                handleQuestionStatusToggle(field.name, event.target.checked)
                              }
                            >
                              <Localize>COMMUNICATION.QuestionStatus_Reviewed</Localize>
                            </Checkbox>
                          </div>
                        </div>

                        <div className="communication-task-form__question-row">
                          <Form.Item
                            className="communication-task-form__question-main"
                            name={[field.name, "text"]}
                            label={<Localize>COMMUNICATION.Field_Question</Localize>}
                            rules={[
                              {
                                required: true,
                                message: localizeByKeyStatic("COMMUNICATION.Validation_Question"),
                              },
                            ]}
                          >
                            <TextArea rows={4} />
                          </Form.Item>

                          <div className="communication-task-form__question-side">
                            <Form.Item
                              name={[field.name, "status"]}
                              label={<Localize>COMMUNICATION.Field_QuestionStatus</Localize>}
                            >
                              <Select suffixIcon={<Icons.Arrow />} options={questionStatusOptions} />
                            </Form.Item>

                            <Button
                              className="light-bg communication-task-form__question-delete"
                              icon={<Icons.Delete />}
                              onClick={() => remove(field.name)}
                            >
                              <span className="ellipsis">
                                <Localize>GLOBAL.Button_Text_Remove</Localize>
                              </span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Form.List>

          {openQuestionsCount > 0 && (
            <div className="communication-task-form__questions-note">
              <span className="communication-task-form__questions-note-icon">
                <Icons.Information />
              </span>
              <span className="communication-task-form__questions-note-text">
                <Localize>COMMUNICATION.Warning_OpenQuestionsCompact</Localize>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="communication-task-form__section">
        <Collapse
          className="communication-original-email-collapse"
          expandIconPosition="end"
          activeKey={openContextPanels}
          onChange={(keys) => setOpenContextPanels(normalizeCollapseKeys(keys as string | string[]))}
          items={[
            {
              key: "ai-context",
              label: originalEmailPanelLabel,
              children: (
                <div className="row-grid grid-gap-10 row-gap-0 communication-original-email-collapse__content">
                  <div className="communication-original-email-collapse__viewer">
                    <CommunicationOriginalEmailField
                      originalBody={task.original_body}
                      equipmentChanges={originalEmailEquipmentChanges}
                      isExpanded={isOriginalEmailExpanded}
                    />
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>

      {auditEntry && (
        <div className="communication-task-form__section">
          <h4><Localize>COMMUNICATION.Section_Audit</Localize></h4>
          <div className="communication-audit-box">
            <div>
              <strong><Localize>TABLES.Column_Title_ApprovedBy</Localize>:</strong> {auditEntry.approved_by}
            </div>
            <div>
              <strong><Localize>TABLES.Column_Title_ApprovedAt</Localize>:</strong>{" "}
              {formatCommunicationDateTime(auditEntry.approved_at)}
            </div>
          </div>
        </div>
      )}

      <div className="form-btn-holder communication-task-form__actions">
        <div className="communication-task-form__actions-start">
          <Button className="light-bg" onClick={() => action?.()}>
            <span className="ellipsis">
              <Localize>GLOBAL.Button_Text_Cancel</Localize>
            </span>
          </Button>

          {currentStep > 1 && (
            <Button
              className="light-bg"
              onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_Back</Localize>
              </span>
            </Button>
          )}
        </div>

        <div className="communication-task-form__actions-end">
          {!isLastStep && (
            <Button
              type="primary"
              onClick={() => setCurrentStep((step) => Math.min(steps.length, step + 1))}
            >
              <span className="ellipsis">
                <Localize>COMMUNICATION.Button_Next</Localize>
              </span>
            </Button>
          )}

          {isLastStep && canApprove && !isAuditView && (
            <Button
              type="primary"
              className="btn-right"
              icon={<Icons.Check />}
              onClick={() => {
                void form.validateFields().then((values) => {
                  onApprove(task.id, values);
                  action?.();
                });
              }}
            >
              <span className="ellipsis">
                <Localize>GLOBAL.Button_Text_Approve</Localize>
              </span>
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};

export default CommunicationTaskForm;
